import React, { useState, useEffect, useRef } from 'react';

/**
 * LazyImage component to lazy load images using IntersectionObserver
 * Features smooth fade-in, skeleton loader, and error fallback.
 */
export default function LazyImage({
  src,
  alt,
  className = "",
  placeholderBg = "bg-slate-100",
  fetchPriority = "auto",
  objectFit = "cover",
  ...props
}) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [inView, setInView] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    // If high priority LCP, load immediately
    if (fetchPriority === "high" || !window.IntersectionObserver) {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: "100px", threshold: 0.01 }
    );

    const currentRef = imgRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      observer.disconnect();
    };
  }, [fetchPriority]);

  const hasPosition = /absolute|relative|fixed|sticky/.test(className);
  const positionClass = hasPosition ? "" : "relative";

  return (
    <div
      ref={imgRef}
      className={`${positionClass} overflow-hidden ${placeholderBg} ${className}`}
      {...props}
    >
      {/* Skeleton loader / pulse shimmer */}
      {!loaded && !error && (
        <div className="absolute inset-0 bg-gradient-to-r from-slate-100 via-slate-200 to-slate-100 animate-pulse z-10" />
      )}

      {error ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400 p-2 text-center text-xs bg-slate-100 z-10">
          <svg className="w-6 h-6 mb-1 opacity-60" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <span>Failed to load</span>
        </div>
      ) : (
        inView && (
          <img
            src={src}
            alt={alt}
            decoding="async"
            fetchpriority={fetchPriority}
            className={`w-full h-full object-${objectFit} transition-all duration-700 ease-out ${
              loaded ? 'opacity-100 scale-100 blur-0' : 'opacity-0 scale-95 blur-[4px]'
            }`}
            onLoad={() => setLoaded(true)}
            onError={() => setError(true)}
          />
        )
      )}
    </div>
  );
}
